
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import {
  feildsRegister,
  initialValuesRegister,
  validationSchemaRegister,
} from "../fromsUtil/register.form";
import { registerWeb, registerWebGoogle } from "../logic/login/action";
import logo from "../images/logo_clock.png";
import registerLogo from "../images/registertransperent.png";
import { FcGoogle } from "react-icons/fc";
import { Link, useNavigate } from "react-router-dom";
import { getCategories } from "../logic/categories/action";
import { signUpWithGoogle } from "../utils/firebase.utils";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { failedAuth, loadingAuth } from "../logic/login/reducer";
import { errorMessage } from "../logic/error/action";
import { motion } from "framer-motion";
import ThreeBackground from "../components/ThreeBackground";
import ModernInput from "../components/ModernInput";
import { MdArrowForward, MdCheckCircle } from "react-icons/md";

const Register = () => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const categoryState = useSelector((state) => state.categories);
  const selectorAuth = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    // Fetch industries from new API endpoint
    const fetchIndustries = async () => {
      try {
        const { getDataSimple } = await import('../utils/serverHelper');
        const data = await getDataSimple('/tenant/industries');
        if (data && Array.isArray(data)) {
          setIndustries(data);
        }
      } catch (error) {
        console.warn('Failed to fetch industries, falling back to categories:', error);
        // Fallback to categories if industries API fails
        getCategories(dispatch, true);
      }
    };
    
    fetchIndustries();
    // Also load categories as fallback
    getCategories(dispatch, true);
  }, [dispatch]);

  const onSubmit = async (values, submitProps) => {
    setIsSubmitting(true);
    await registerWeb(dispatch, values, nav);
    submitProps.setSubmitting(false);
    setIsSubmitting(false);
  };

  const registerWithGoogle = async () => {
    try {
      dispatch(loadingAuth());
      const googleData = await signUpWithGoogle();
      if (googleData) {
        const req = {
          email: googleData.email,
          google_id: googleData.google_id,
          name: googleData.name,
          access_token: googleData.access_token,
          contact: "",
          google_headers: JSON.stringify({
            Authorization: `Bearer ${googleData.access_token}`,
          }),
          fcm: "fcm",
        };
        registerWebGoogle(dispatch, req, nav);
      } else {
        dispatch(failedAuth({ message: "Please select account" }));
        errorMessage(dispatch, "Please select account");
      }
    } catch (error) {
      console.log(error);
      dispatch(failedAuth({ message: "Error signing up with Google" }));
      errorMessage(dispatch, "Error signing up with Google");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const features = [
    "Manage Staff & Patients",
    "Book Appointments Easily",
    "Grow Your Business",
    "Stay on Top of Schedule",
  ];

  // Format business type label to be more readable
  const formatBusinessTypeLabel = (businessType) => {
    if (!businessType) return "";
    // Replace underscores with spaces and convert to Title Case
    return businessType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  };

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-[#667eea] via-[#764ba2] to-[#0B469D] relative overflow-hidden flex flex-col">
      <ThreeBackground />

      {/* Loading Overlay */}
      {selectorAuth.status === "loading" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-2xl p-8 shadow-2xl mx-4 flex flex-col items-center justify-center min-w-[200px]"
          >
            <CircularProgress size={60} sx={{ color: "#0B469D" }} />
            <Typography
              className="mt-4 text-gray-700 text-sm text-center"
              sx={{ textAlign: "center" }}
            >
              Creating your account...
            </Typography>
          </motion.div>
        </motion.div>
      )}

      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full flex items-center py-2 px-3 sm:px-4 md:px-6 lg:px-8 flex-shrink-0 relative z-10"
      >
        <motion.a
          href="/"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-shrink-0"
        >
          <img
            src={logo}
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 drop-shadow-lg"
            alt="Logo"
          />
        </motion.a>
      </motion.header>

      {/* Main Content */}
      <div className="w-full flex-1 flex items-center px-2 sm:px-3 md:px-4 lg:px-6 relative z-10 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center w-full h-full max-w-7xl mx-auto">
          {/* Left Side - Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="hidden lg:flex flex-col gap-2 sm:gap-3 text-white order-2 lg:order-1 h-full justify-center"
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: "2.5rem", xl: "3rem" },
                  fontWeight: "bold",
                  lineHeight: 1.2,
                  mb: 2,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Streamline Business Activities
                <span className="text-[#72a9fb]">.</span>
              </Typography>
            </motion.div>

            <motion.p
              variants={itemVariants}
              sx={{
                fontSize: { lg: "1rem", xl: "1.125rem" },
                lineHeight: 1.5,
                color: "rgba(255, 255, 255, 0.9)",
                mb: 2,
              }}
            >
              Stay on top of your Schedule, Manage Staff and Patients, Book
              Appointments and Grow Your Business.
            </motion.p>

            <motion.ul variants={itemVariants} className="space-y-1.5">
              {features.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 text-white/90 text-sm lg:text-base"
                >
                  <MdCheckCircle className="text-[#72a9fb] flex-shrink-0 text-lg" />
                  <span>{feature}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center w-full order-1 lg:order-2 h-full items-center"
          >
            <motion.div
              transition={{ duration: 0.3 }}
              className="w-full max-w-md lg:max-w-lg flex items-center"
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.98)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: { xs: 2.5, sm: 3, md: 3.5 },
                  boxShadow:
                    "0 25px 70px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: { xs: "100%", lg: "fit-content" },
                  maxHeight: { xs: "100%", lg: "90vh" },
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-col h-full"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: {
                        xs: "1.375rem",
                        sm: "1.625rem",
                        md: "1.875rem",
                      },
                      fontWeight: 700,
                      color: "#111827",
                      mb: 1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Get your account now!
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.8125rem",
                        md: "0.875rem",
                      },
                      color: "#4b5563",
                      mb: { xs: 2, sm: 2.5 },
                    }}
                  >
                    Start managing your business today
                  </Typography>

                  {/* Form */}
                  <div className="flex-1 flex flex-col min-h-0">
                    <Formik
                      initialValues={initialValuesRegister}
                      validationSchema={validationSchemaRegister}
                      onSubmit={onSubmit}
                    >
                      {({ isSubmitting: formSubmitting, isValid }) => (
                        <Form className="flex flex-col flex-1 min-h-0">
                          <div className="flex-1 overflow-y-auto pr-1 -mr-1 min-h-0">
                            <div className="flex flex-col gap-3 sm:gap-4 pt-[10px]">
                              {(() => {
                                // Prefer industries from API, fallback to categories
                                let categoryOptions = [];
                                
                                if (industries && industries.length > 0) {
                                  // Use industries from API
                                  categoryOptions = industries.map((industry) => {
                                    return {
                                      value: industry.industry_id, // Use industry_id
                                      label: industry.name,
                                      disabled: false,
                                    };
                                  });
                                } else if (
                                  categoryState?.data &&
                                  Array.isArray(categoryState.data) &&
                                  categoryState.data.length > 0
                                ) {
                                  // Fallback to categories (presets)
                                  categoryOptions = categoryState.data.map((e) => {
                                        return {
                                          value: e.preset_id,
                                          label: formatBusinessTypeLabel(
                                            e.business_type
                                          ),
                                          disabled: false,
                                        };
                                  });
                                }

                                const formFields =
                                  feildsRegister(categoryOptions);

                                return formFields;
                              })().map((field, index) => {
                                return (
                                  <motion.div
                                    key={field.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 + index * 0.05 }}
                                  >
                                    <ModernInput
                                      comp={field}
                                      isSelect={field.type === "select"}
                                      options={field.options || []}
                                    />
                                  </motion.div>
                                );
                              })}
                            </div>
                          </div>

                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                            className="mt-auto pt-2 flex-shrink-0"
                          >
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <motion.div
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                className="flex-1"
                              >
                                <Button
                                  type="submit"
                                  disabled={
                                    formSubmitting || isSubmitting || !isValid
                                  }
                                  fullWidth
                                  variant="contained"
                                  size="small"
                                  endIcon={<MdArrowForward size={18} />}
                                  sx={{
                                    borderRadius: "10px",
                                    py: { xs: 0.875, sm: 1.125 },
                                    background:
                                      "linear-gradient(135deg, #0B469D 0%, #72a9fb 100%)",
                                    textTransform: "none",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                    fontWeight: 600,
                                    boxShadow:
                                      "0 6px 20px rgba(11, 70, 157, 0.35), 0 2px 8px rgba(11, 70, 157, 0.2)",
                                    "&:hover": {
                                      background:
                                        "linear-gradient(135deg, #0a3d7a 0%, #5a8fd8 100%)",
                                      boxShadow:
                                        "0 8px 25px rgba(11, 70, 157, 0.45), 0 4px 12px rgba(11, 70, 157, 0.25)",
                                      transform: "translateY(-1px)",
                                    },
                                    "&:disabled": {
                                      background:
                                        "linear-gradient(135deg, #9ca3af 0%, #9ca3af 100%)",
                                      opacity: 1,
                                      cursor: "not-allowed",
                                      pointerEvents: "none",
                                    },
                                  }}
                                >
                                  {formSubmitting || isSubmitting
                                    ? "Creating Account..."
                                    : "Create Account"}
                                </Button>
                              </motion.div>

                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1 }}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                className="flex-1"
                              >
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={registerWithGoogle}
                                  size="small"
                                  startIcon={<FcGoogle size={20} />}
                                  sx={{
                                    borderRadius: "10px",
                                    py: { xs: 0.875, sm: 1.125 },
                                    borderColor: "#d1d5db",
                                    color: "#374151",
                                    textTransform: "none",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                    fontWeight: 500,
                                    backgroundColor: "white",
                                    "&:hover": {
                                      borderColor: "#0B469D",
                                      backgroundColor:
                                        "rgba(11, 70, 157, 0.05)",
                                      color: "#0B469D",
                                    },
                                  }}
                                >
                                  Continue with Google
                                </Button>
                              </motion.div>
                            </div>
                          </motion.div>
                        </Form>
                      )}
                    </Formik>
                  </div>

                  <div className="pt-3 border-t border-gray-200 mt-3 flex-shrink-0">
                    {/* Sign In Link */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.1 }}
                      className="text-center"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b7280",
                          fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        }}
                      >
                        Already have an account?{" "}
                        <Link
                          to="/admin/login"
                          className="text-[#0B469D] hover:text-[#72a9fb] font-semibold transition-colors underline decoration-transparent hover:decoration-[#0B469D]"
                        >
                          Sign in
                        </Link>
                      </Typography>
                    </motion.div>
                  </div>
                </motion.div>
              </Box>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Register;
